import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ProfileData } from '../models/user.model';
import { map, tap } from 'rxjs/operators';
import { ApiResponse } from '../models/response.model';
import { SupportListResponse } from '../models/ticket.model';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class ProfileService {

  profile = new BehaviorSubject<ProfileData | null>(null);
  profile$ = this.profile.asObservable();

  _profileEndpoint = `${environment.apiBaseUrl}/user/v1/customer/`;

  constructor(
    private _http: HttpClient,
    private auth: AuthService
  ) {
    if (auth.getCurrentUser()) this.getProfile();
  }

  signup(data: any): Observable<any> {
    return this._http.post<ApiResponse<any>>(this._profileEndpoint, data)
      .pipe(map((res) => res.data));
  }

  getProfile() {
    this._http
      .get<ApiResponse<any>>(this._profileEndpoint + 'profile')
      .pipe(map((res) => res.data)).subscribe((data) => {
        this.profile.next(data);
      });
  }

  getProfileData(): Observable<any> {
    return this._http
      .get<ApiResponse<any>>(this._profileEndpoint + 'profile')
      .pipe(map((res) => res.data));
  }

  updatePersonalInfo(params: any) {
    return this._http
      .patch<ApiResponse<any>>(`${this._profileEndpoint}/personal-info`, params)
      .pipe(map((res) => res.data), tap((data) => this.profile.next(data)));
  }

  sendVerifyContactOtp(bankingMobile: string) {
    return this._http
      .patch<ApiResponse<any>>(`${this._profileEndpoint}/send-verify-contact-otp`, {
        bankingMobile,
      }).pipe(map((res) => res.data));
  }

  updateContactDetails(params: any) {
    return this._http
      .patch<ApiResponse<any>>(`${this._profileEndpoint}/contact-details`, params)
      .pipe(map((res) => res.data), tap((data) => this.profile.next(data)));
  }

  acceptQuotation(params: any) {
    return this._http
      .patch<ApiResponse<any>>(`${environment.apiBaseUrl}/user/v1/subscriptions/accept-quotation`, params)
      .pipe(map((res) => res.data));
  }

  payToken(params: any) {
    return this._http
      .post<ApiResponse<any>>(`${environment.apiBaseUrl}/user/v1/payments/make-payment`, params)
      .pipe(map((res) => res.data));
  }

  updatePaymentResponse(params: any) {
    return this._http
      .post<ApiResponse<any>>(`${environment.apiBaseUrl}/user/v1/payments/handle-payment-response`, params)
      .pipe(map((res) => res.data));
  }

  addSupportRequest(params: any) {
    return this._http
      .post<ApiResponse<any>>(`${environment.apiBaseUrl}/user/v1/support`, params)
      .pipe(map((res) => res));
  }

  getSupportRequests(): Observable<SupportListResponse> {
    return this._http
      .get<ApiResponse<any>>(`${environment.apiBaseUrl}/user/v1/support`)
      .pipe(map((res) => {
        return res.data;
      }));
  }

  acceptAgreement(subscriptionId: string) {
    return this._http
      .patch<ApiResponse<any>>(`${environment.apiBaseUrl}/user/v1/subscriptions/accept-agreement`, { subscriptionId, })
      .pipe(map((res) => res));
  }

  acceptVehicleCondition(subscriptionId: string) {
    return this._http
      .patch<ApiResponse<any>>(`${environment.apiBaseUrl}/user/v1/subscriptions/accept-vehicle-condition`, { subscriptionId, })
      .pipe(map((res) => res));
  }

  optWhatsAppNotification(customerId: any, body: any) {
    return this._http
      .patch<ApiResponse<any>>(`${this._profileEndpoint}whatsapp-preference-change/${customerId}`, body)
      .pipe(map((res) => res));
  }

  updateCreditConsent(hasCreditFetchConsent: boolean): Observable<any> {
    return this._http.patch<ApiResponse<any>>(`${this._profileEndpoint}credit-consent/`,
      { hasCreditFetchConsent, }
    ).pipe(tap(() => {
      this.getProfile();
    }));
  }
}
